import {BaseControl, FilterControl, Helper, SelectControl} from 'appShared/classes';
import {ComponentTypesEnum} from 'appShared/enums/common';
import {ReportTableColumn, ReportTableConfigInterface, ReportTableData} from 'appShared/components/report-table';
import {environment} from 'appEnv/environment';
import {FeedUsageReportsGroupByTypesEnum, MarketDataUsageReportsFilterModel} from '@spa/api';
import {Pagination} from 'appShared/components';

export const MarketDataUsageReportsStorageId = 'MarketDataUsageReportsComponent';

export const MarketDataUsageReportsGroupBySelector = 'MarketDataUsageReportsGroupBy';

export const MarketDataUsageReportsClientSelector = 'MarketDataUsageReportsClient';
export const MarketDataUsageReportsMonthSelector = 'MarketDataUsageReportsMonth';
export const MarketDataUsageReportsUserSelector = 'MarketDataUsageReportsUser';
export const MarketDataUsageReportsAccountSelector = 'MarketDataUsageReportsAccount';
export const MarketDataUsageReportsBundleSelector = 'MarketDataUsageReportsBundle';
export const MarketDataUsageReportsDateFromSelector = 'MarketDataUsageReportsDateFrom';
export const MarketDataUsageReportsDateToSelector = 'MarketDataUsageReportsDateTo';
export const MarketDataUsageReportsDateSelector = 'MarketDataUsageReportsDate';

export const MarketDataUsageReportsNumberOfActiveBundlesSelector = 'MarketDataUsageReportsNumberOfActiveBundles';
export const MarketDataUsageReportsTotalMarkupSelector = 'MarketDataUsageReportsTotalMarkup';
export const MarketDataUsageReportsTotalPriceSelector = 'MarketDataUsageReportsPotalPrice';

export const MarketDataUsageReportsGroupByLabel = 'Columns';
export const MarketDataUsageReportsAccountName = 'Account';
export const MarketDataUsageReportsUserName = 'User';
export const MarketDataUsageReportsClientName = 'Client';
export const MarketDataUsageReportsFeedBundleName = 'Feed/Bundle';

export const DEFAULT_USAGE_REPORTS_GROUPPING_VALUES = [
    FeedUsageReportsGroupByTypesEnum.USER_ID,
    FeedUsageReportsGroupByTypesEnum.ACCOUNT_ID,
    FeedUsageReportsGroupByTypesEnum.BUNDLE_NAME,
    FeedUsageReportsGroupByTypesEnum.MONTH,
];

export const MarketDataUsageReportsFilterControls = (
    hasAccountView: boolean,
    hasBundleView: boolean
): FilterControl[] => {
    const controls = [
        new FilterControl({
            name: MarketDataUsageReportsDateSelector,
            placeholder: '',
            controls: [
                new BaseControl({
                    name: MarketDataUsageReportsDateFromSelector,
                    value: Helper.formatDateByValueOrStartOfMonth(
                        null,
                        environment.dateFormatLocalDate,
                        true
                    ),
                    placeholder: 'Date from',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 1
                }),
                new BaseControl({
                    name: MarketDataUsageReportsDateToSelector,
                    value: Helper.formatDateByValueOrNow(null, environment.dateFormatLocalDate, true),
                    placeholder: 'Date to',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                }),
            ],
            sortIndex: 0,
            shown: true,
            isRemovable: false
        })
    ];

    if (hasAccountView) {
        controls.push(new FilterControl({
            name: MarketDataUsageReportsAccountSelector,
            placeholder: MarketDataUsageReportsAccountName,
            controls: [
                new BaseControl({
                    name: MarketDataUsageReportsAccountSelector,
                    value: null,
                    placeholder: MarketDataUsageReportsAccountName,
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                }),
            ],
            sortIndex: 0,
            shown: true,
            isRemovable: false
        }));
    }

    if (hasBundleView) {
        controls.push(new FilterControl({
            name: MarketDataUsageReportsBundleSelector,
            placeholder: MarketDataUsageReportsFeedBundleName,
            controls: [
                new SelectControl({
                    name: MarketDataUsageReportsBundleSelector,
                    value: null,
                    placeholder: MarketDataUsageReportsFeedBundleName,
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [],
                    multiple: true,
                }),
            ],
            sortIndex: 0,
            shown: true,
            isRemovable: false
        }));
    }

    const groupByFilterControl = {
        name: MarketDataUsageReportsGroupBySelector,
        placeholder: MarketDataUsageReportsGroupByLabel,
        controls: [
            new SelectControl({
                name: MarketDataUsageReportsGroupBySelector,
                value: null,
                placeholder: MarketDataUsageReportsGroupByLabel,
                validators: [],
                componentType: ComponentTypesEnum.Select,
                options: [
                    {
                        value: FeedUsageReportsGroupByTypesEnum.USER_ID,
                        label: Helper.textsHelper().getMarketDataUsageReportsGroupingName(FeedUsageReportsGroupByTypesEnum.USER_ID),
                    },
                    {
                        value: FeedUsageReportsGroupByTypesEnum.ACCOUNT_ID,
                        label: Helper.textsHelper().getMarketDataUsageReportsGroupingName(FeedUsageReportsGroupByTypesEnum.ACCOUNT_ID),
                    },
                    {
                        value: FeedUsageReportsGroupByTypesEnum.MONTH,
                        label: Helper.textsHelper().getMarketDataUsageReportsGroupingName(FeedUsageReportsGroupByTypesEnum.MONTH),
                    },
                    {
                        value: FeedUsageReportsGroupByTypesEnum.BUNDLE_NAME,
                        label: Helper.textsHelper().getMarketDataUsageReportsGroupingName(FeedUsageReportsGroupByTypesEnum.BUNDLE_NAME),
                    },
                ],
                multiple: true,
            }),
        ],
        sortIndex: 0,
        shown: true,
        isRemovable: false
    };

    groupByFilterControl.controls[0].value = DEFAULT_USAGE_REPORTS_GROUPPING_VALUES;
    controls.push(new FilterControl(groupByFilterControl));

    return controls;
};

export const MarketDataUsageReportsDataTableItems = (): ReportTableData[] => {
    return [
        new ReportTableData<number>({
            colId: MarketDataUsageReportsClientSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MarketDataUsageReportsMonthSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MarketDataUsageReportsAccountSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MarketDataUsageReportsBundleSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MarketDataUsageReportsUserSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MarketDataUsageReportsTotalPriceSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MarketDataUsageReportsNumberOfActiveBundlesSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MarketDataUsageReportsTotalMarkupSelector,
            value: null,
            hidden: false
        })
    ];
};

export const MarketDataUsageReportsDataTableConfig = (): ReportTableConfigInterface => {
    const columns = [
        new ReportTableColumn({
            label: 'Client',
            selector: MarketDataUsageReportsClientSelector
        }),
        new ReportTableColumn({
            label: 'Month',
            selector: MarketDataUsageReportsMonthSelector
        }),
        new ReportTableColumn({
            label: 'Account',
            selector: MarketDataUsageReportsAccountSelector
        }),
        new ReportTableColumn({
            label: MarketDataUsageReportsFeedBundleName,
            selector: MarketDataUsageReportsBundleSelector
        }),
        new ReportTableColumn({
            label: 'User',
            selector: MarketDataUsageReportsUserSelector
        }),
        new ReportTableColumn({
            label: 'Total Fees',
            selector: MarketDataUsageReportsTotalPriceSelector
        }),
        new ReportTableColumn({
            label: 'Total Feed/Bundle',
            selector: MarketDataUsageReportsNumberOfActiveBundlesSelector
        }),
        new ReportTableColumn({
            label: 'Total Rebates',
            selector: MarketDataUsageReportsTotalMarkupSelector
        })
    ];

    return {
        rows: [],
        columns,
        hiddenColumnIds: [],
        actionMenu: false,
        disableColumnsOptions: false,
    };
};

export const defaultMarketDataUsageReportsInitialFilterModel = (itemsPerPage = Pagination.defaultItemsPerPage) => {
    const filter = new MarketDataUsageReportsFilterModel(
        Helper.formatDateByValueOrStartOfMonth(null, environment.dateFormatLocalDate, true),
        Helper.formatDateByValueOrNow(null, environment.dateFormatLocalDate, true),
        Pagination.defaultPagesChunk - 1,
        itemsPerPage
    );

    filter.groupBys = DEFAULT_USAGE_REPORTS_GROUPPING_VALUES;

    return filter;
};
